import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Avatar } from "../components/Layout";
import { Camera, Check, Shield, Crown, UserRound, Save } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  central_admin: "Central Admin",
  chapter_lead: "Leader",
  member: "Member",
};

const ROLE_ICONS = { central_admin: Crown, chapter_lead: Shield, member: UserRound };

const HYPHENATES = ["Leader", "Strategist", "Creator", "Consultant", "Entrepreneur", "Advisor", "Educator", "Speaker", "Writer", "Technologist", "Community Builder", "Artist", "Researcher"];
const EXPLORING = ["Promotion", "Career Change", "Fractional Work", "Consulting", "Entrepreneurship", "Content Creation", "Speaking", "Board/Advisory Work", "Learning/Credential", "Creative Project", "Something Else"];

function Pills({ label, items, field, single, form, setForm }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold">{label}</p>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => {
          const active = single ? form[field] === item : form[field].includes(item);
          return (
            <button key={item} type="button" data-testid={`profile-${field}-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() =>
                single
                  ? setForm((f) => ({ ...f, [field]: item }))
                  : setForm((f) => ({ ...f, [field]: f[field].includes(item) ? f[field].filter((v) => v !== item) : [...f[field], item] }))
              }
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 flex items-center gap-1.5 ${active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
              {active && <Check className="h-3 w-3" />}
              {item}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, refresh } = useAuth();
  const [opts, setOpts] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", title: "", company: "", industry: "", business_stage: "", goals: [], values: [], availability: "", bio: "", what_i_know: "", know_tags: [], hyphenates: [], exploring: [] });
  const [knowTag, setKnowTag] = useState("");
  const [customHyphenate, setCustomHyphenate] = useState("");

  useEffect(() => {
    setForm({
      name: user.name || "", title: user.title || "", company: user.company || "",
      industry: user.industry || "", business_stage: user.business_stage || "",
      goals: user.goals || [], values: user.values || [], availability: user.availability || "", bio: user.bio || "",
      what_i_know: user.what_i_know || "", know_tags: user.know_tags || [],
      hyphenates: user.hyphenates || [], exploring: user.exploring || [],
    });
  }, [user.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get("/meta/options").then((r) => setOpts(r.data));
    api.get("/chapters").then((r) => setChapters(r.data)).catch(() => {});
    if (user.role === "central_admin" || user.role === "chapter_lead") {
      api.get("/admin/overview").then((r) => setStats(r.data)).catch(() => {});
    } else {
      api.get("/dashboard").then((r) => setStats(r.data)).catch(() => {});
    }
  }, [user.user_id, user.role]);

  const myChapter = chapters.find((c) => c.chapter_id === user.chapter_id);
  const leadChapter = chapters.find((c) => c.lead?.user_id === user.user_id || c.lead_user_id === user.user_id);
  const RoleIcon = ROLE_ICONS[user.role] || UserRound;

  const toggle = (key, val) => {
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val] }));
  };

  const addKnowTag = () => {
    const t = knowTag.trim();
    if (!t || form.know_tags.includes(t)) return;
    setForm((f) => ({ ...f, know_tags: [...f.know_tags, t] }));
    setKnowTag("");
  };

  const addHyphenate = () => {
    const t = customHyphenate.trim();
    if (!t || form.hyphenates.includes(t)) return;
    setForm((f) => ({ ...f, hyphenates: [...f.hyphenates, t] }));
    setCustomHyphenate("");
  };

  const save = async () => {    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.put("/profile/intake", form);
      await refresh();
      toast.success("Profile saved");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads/image", fd);
      await api.put("/profile/picture", { image_path: res.data.path });
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div data-testid="profile-page" className="space-y-8">
      <PageHeader eyebrow={ROLE_LABELS[user.role] || "Member"} title="My Profile" description="There's more to you than a title. Tell Bouquet what you know, what you're exploring, and how you'd like to grow." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        <div className="space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 space-y-4 text-center" data-testid="profile-identity-card">
            <div className="relative inline-block">
              <Avatar user={user} size="h-24 w-24" />
              <input data-testid="profile-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" id="profile-photo-file" onChange={handlePhoto} />
              <label htmlFor="profile-photo-file" data-testid="profile-photo-upload-button"
                className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity">
                <Camera className="h-4 w-4" />
              </label>
            </div>
            {uploading && <p className="text-xs text-muted-foreground">Uploading photo…</p>}
            <div>
              <p className="font-display text-xl font-medium">{user.name}</p>
              <p className="text-sm text-muted-foreground">{user.title}{user.company ? ` · ${user.company}` : ""}</p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-semibold" data-testid="profile-role-badge">
                <RoleIcon className="h-3 w-3" /> {ROLE_LABELS[user.role] || user.role}
              </span>
              {myChapter && <span className="px-3 py-1.5 rounded-full bg-secondary text-[11px] font-semibold" data-testid="profile-chapter-badge">{myChapter.name}</span>}
              {user.tier && <span className="px-3 py-1.5 rounded-full bg-accent/15 border border-accent/30 text-[11px] font-semibold" data-testid="profile-tier-badge">{user.tier}</span>}
            </div>
            <p className="text-xs text-muted-foreground">{user.email}</p>
          </div>

          {user.role === "central_admin" && stats && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="profile-admin-scope">
              <p className="text-sm font-semibold">Oversight Scope</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="font-display text-xl font-medium">{stats.chapters}</p><p className="text-[11px] text-muted-foreground">Chapters</p></div>
                <div><p className="font-display text-xl font-medium">{stats.members}</p><p className="text-[11px] text-muted-foreground">Active members</p></div>
                <div><p className="font-display text-xl font-medium">{stats.pending_approvals}</p><p className="text-[11px] text-muted-foreground">Pending approvals</p></div>
                <div><p className="font-display text-xl font-medium">${stats.fund_balance.toLocaleString()}</p><p className="text-[11px] text-muted-foreground">Central fund</p></div>
              </div>
            </div>
          )}

          {user.role === "chapter_lead" && stats && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="profile-lead-scope">
              <p className="text-sm font-semibold">House Mother Scope</p>
              {leadChapter && <p className="text-sm text-muted-foreground">{leadChapter.name} · {leadChapter.member_count} members</p>}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="font-display text-xl font-medium">{stats.members}</p><p className="text-[11px] text-muted-foreground">Chapter members</p></div>
                <div><p className="font-display text-xl font-medium">{stats.checkins_this_week}</p><p className="text-[11px] text-muted-foreground">Check-ins this week</p></div>
                <div><p className="font-display text-xl font-medium">{stats.dues_paid}/{stats.dues_total}</p><p className="text-[11px] text-muted-foreground">Dues paid</p></div>
                <div><p className="font-display text-xl font-medium">{stats.pending_fund_requests}</p><p className="text-[11px] text-muted-foreground">Fund requests out</p></div>
              </div>
            </div>
          )}

          {user.role === "member" && stats && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-3" data-testid="profile-member-scope">
              <p className="text-sm font-semibold">Membership Snapshot</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="font-display text-xl font-medium">{stats.value_index}</p><p className="text-[11px] text-muted-foreground">Value index</p></div>
                <div><p className="font-display text-xl font-medium">{stats.pod ? stats.pod.name : "—"}</p><p className="text-[11px] text-muted-foreground">Pod</p></div>
                <div><p className="font-display text-xl font-medium">{stats.stats.wins}</p><p className="text-[11px] text-muted-foreground">Wins logged</p></div>
                <div><p className="font-display text-xl font-medium">{stats.stats.checkins}</p><p className="text-[11px] text-muted-foreground">Check-ins</p></div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6 sm:p-8 space-y-6" data-testid="profile-edit-form">
          <p className="font-display text-lg font-medium">Edit Profile</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold block mb-1.5">Display name</label>
              <input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">How do you describe what you do?</label>
              <input data-testid="profile-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="I help founders turn ideas into operating businesses" className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">Company</label>
              <input data-testid="profile-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className="text-sm font-semibold block mb-1.5">Availability</label>
              <select data-testid="profile-availability-select" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className={inputCls}>
                <option value="">Select…</option>
                {(opts?.availability || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {opts && (
            <>
              <Pills label="Industry" items={opts.industries} field="industry" single form={form} setForm={setForm} />
              <Pills label="Goals" items={opts.goals} field="goals" form={form} setForm={setForm} />
              <Pills label="Values" items={opts.values} field="values" form={form} setForm={setForm} />
            </>
          )}

          <div className="space-y-2">
            <Pills label="My Hyphenates" items={[...new Set([...HYPHENATES, ...form.hyphenates])]} field="hyphenates" form={form} setForm={setForm} />
            <div className="flex gap-2">
              <input data-testid="profile-hyphenate-custom-input" value={customHyphenate} onChange={(e) => setCustomHyphenate(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHyphenate(); } }}
                placeholder="Add your own…" className={inputCls} />
              <button type="button" data-testid="profile-hyphenate-custom-add" onClick={addHyphenate}
                className="px-3.5 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition-colors shrink-0">Add</button>
            </div>
          </div>

          <Pills label="What are you exploring right now?" items={EXPLORING} field="exploring" form={form} setForm={setForm} />

          <div className="space-y-2">
            <label className="text-sm font-semibold block">What I Know</label>
            <p className="text-xs text-muted-foreground">What topics, problems, skills, or experiences do people come to you for?</p>
            <textarea data-testid="profile-what-i-know-input" value={form.what_i_know} onChange={(e) => setForm({ ...form, what_i_know: e.target.value })} rows={2}
              placeholder="Pricing strategy, first hires, pitching investors…" className={inputCls} />
            <div className="flex gap-2">
              <input data-testid="profile-know-tag-input" value={knowTag} onChange={(e) => setKnowTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKnowTag(); } }}
                placeholder="Add a tag (optional)…" className={inputCls} />
              <button type="button" data-testid="profile-know-tag-add" onClick={addKnowTag}
                className="px-3.5 py-2 rounded-lg border border-border text-xs font-semibold hover:bg-secondary transition-colors shrink-0">Add</button>
            </div>
            {form.know_tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {form.know_tags.map((t) => (
                  <span key={t} data-testid={`profile-know-tag-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-secondary text-xs font-medium">
                    {t}
                    <button type="button" data-testid={`profile-know-tag-remove-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, know_tags: f.know_tags.filter((x) => x !== t) }))}
                      className="text-muted-foreground hover:text-foreground">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold block mb-1.5">Bio</label>
            <textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} rows={3}
              placeholder="What you're building, what you need, what you offer…" className={inputCls} />
          </div>

          <button data-testid="profile-save-button" onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
